import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Calendar } from "lucide-react";
import { authMe, authLogout, adminFetch, formatErr } from "./adminApi";
import { Seo } from "../seo/Seo";

const Tab = ({ value, current, onClick, children }) => (
  <button
    data-testid={`admin-tab-${value}`}
    onClick={() => onClick(value)}
    className={`px-5 py-3 font-mono-ui text-[11px] tracking-[0.25em] uppercase border-b-2 transition-colors ${
      current === value ? "border-[#C8102E] text-[#0a0a0a]" : "border-transparent text-black/55 hover:text-[#0a0a0a]"
    }`}
  >
    {children}
  </button>
);

const Stat = ({ label, value }) => (
  <div className="border border-black/10 p-6">
    <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/45">{label}</p>
    <p className="mt-4 font-display text-4xl md:text-5xl tracking-[-0.02em]">{value}</p>
  </div>
);

const TableHead = ({ cols }) => (
  <div className="grid grid-cols-12 gap-4 py-3 border-b border-black/15">
    {cols.map((c) => (
      <div key={c.label} className={`${c.cls} font-mono-ui text-[10px] tracking-[0.25em] uppercase text-black/45`}>{c.label}</div>
    ))}
  </div>
);

const RankRows = ({ rows, labelKey, max }) => {
  const top = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="space-y-2">
      {rows.slice(0, max ?? 10).map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-3 items-center">
          <div className="col-span-8 truncate text-sm text-[#0a0a0a]/85">{r[labelKey] || "—"}</div>
          <div className="col-span-3 h-1.5 bg-black/10 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-[#C8102E]" style={{ width: `${(r.count / top) * 100}%` }} />
          </div>
          <div className="col-span-1 text-right font-mono-ui text-[11px] text-black/65">{r.count}</div>
        </div>
      ))}
      {rows.length === 0 && <p className="text-black/35 text-sm">No data yet.</p>}
    </div>
  );
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
function daysAgoISO(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [newsletter, setNewsletter] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Date range: default to all-time (empty)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    authMe()
      .then(() => loadAll())
      .catch(() => nav("/admin/login", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [a, n, b] = await Promise.all([
        adminFetch.analytics(startDate || undefined, endDate || undefined),
        adminFetch.newsletter(),
        adminFetch.bookings(),
      ]);
      setAnalytics(a); setNewsletter(n); setBookings(b);
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const applyRange = async () => {
    setLoading(true); setError("");
    try {
      const a = await adminFetch.analytics(startDate || undefined, endDate || undefined);
      setAnalytics(a);
    } catch (err) { setError(formatErr(err)); }
    finally { setLoading(false); }
  };

  const quickRange = (kind) => {
    if (kind === "all") { setStartDate(""); setEndDate(""); }
    else if (kind === "30") { setStartDate(daysAgoISO(30)); setEndDate(todayISO()); }
    else if (kind === "7")  { setStartDate(daysAgoISO(7));  setEndDate(todayISO()); }
    else if (kind === "today") { setStartDate(todayISO()); setEndDate(todayISO()); }
  };

  const logout = async () => {
    try { await authLogout(); } catch {}
    nav("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-white text-[#0a0a0a]">
      <Seo title="Admin Dashboard — Manuel Santana" description="Admin." path="/admin" schemas={[]} />

      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-black/10">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-[68px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[#C8102E]">MS</span>
            <span className="font-display text-xl">Admin · Manuel Santana</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={loadAll} disabled={loading} className="inline-flex items-center gap-2 border border-black/20 hover:bg-[#0a0a0a] hover:text-white px-3 py-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition disabled:opacity-40">
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 border border-black/20 hover:bg-[#C8102E] hover:border-[#C8102E] hover:text-white px-3 py-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition">
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 flex gap-2">
          <Tab value="analytics" current={tab} onClick={setTab}>Analytics</Tab>
          <Tab value="newsletter" current={tab} onClick={setTab}>Newsletter ({newsletter.length})</Tab>
          <Tab value="inquiries" current={tab} onClick={setTab}>Inquiries ({bookings.length})</Tab>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-6 md:px-10 py-10">
        {error && <p data-testid="admin-error" className="mb-6 font-mono-ui text-[11px] tracking-[0.2em] uppercase text-[#C8102E]">{error}</p>}

        {tab === "analytics" && (
          <div className="space-y-10">
            {/* Date range */}
            <div className="flex flex-wrap items-end gap-4 border border-black/10 p-5">
              <Calendar size={16} className="text-[#C8102E]" />
              <div>
                <label className="font-mono-ui text-[9px] tracking-[0.3em] uppercase text-black/45 block mb-1">Start</label>
                <input data-testid="admin-range-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-transparent border border-black/20 px-3 py-2 text-sm text-[#0a0a0a]" />
              </div>
              <div>
                <label className="font-mono-ui text-[9px] tracking-[0.3em] uppercase text-black/45 block mb-1">End</label>
                <input data-testid="admin-range-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-transparent border border-black/20 px-3 py-2 text-sm text-white" />
              </div>
              <button data-testid="admin-range-apply" onClick={applyRange} className="bg-[#0a0a0a] text-white hover:bg-[#C8102E] px-5 py-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition">Apply</button>
              <div className="flex gap-2 flex-wrap ml-auto">
                <button data-testid="admin-quick-all" onClick={() => { quickRange("all"); setTimeout(applyRange, 0); }} className="border border-black/20 hover:bg-[#0a0a0a] hover:text-white px-3 py-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition">All Time</button>
                <button data-testid="admin-quick-30" onClick={() => { quickRange("30"); setTimeout(applyRange, 0); }} className="border border-black/20 hover:bg-[#0a0a0a] hover:text-white px-3 py-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition">30 days</button>
                <button data-testid="admin-quick-7" onClick={() => { quickRange("7"); setTimeout(applyRange, 0); }} className="border border-black/20 hover:bg-[#0a0a0a] hover:text-white px-3 py-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition">7 days</button>
                <button data-testid="admin-quick-today" onClick={() => { quickRange("today"); setTimeout(applyRange, 0); }} className="border border-black/20 hover:bg-[#0a0a0a] hover:text-white px-3 py-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition">Today</button>
              </div>
            </div>

            {/* Totals */}
            {analytics && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Pageviews" value={analytics.totals.pageviews} />
                <Stat label="Unique Visitors" value={analytics.totals.unique_visitors} />
                <Stat label="Newsletter Signups" value={analytics.totals.newsletter_signups} />
                <Stat label="Inquiries" value={analytics.totals.booking_inquiries} />
              </div>
            )}

            {/* Charts */}
            {analytics && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <section className="border border-black/10 p-6">
                  <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#C8102E] mb-6">— Top Pages</p>
                  <RankRows rows={analytics.top_pages} labelKey="path" max={12} />
                </section>
                <section className="border border-black/10 p-6">
                  <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#C8102E] mb-6">— Top Referrers</p>
                  <RankRows rows={analytics.top_referrers} labelKey="referrer" max={10} />
                </section>
                <section className="border border-black/10 p-6">
                  <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#C8102E] mb-6">— Devices</p>
                  <RankRows rows={analytics.devices} labelKey="device" />
                </section>
                <section className="border border-black/10 p-6">
                  <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#C8102E] mb-6">— Browsers</p>
                  <RankRows rows={analytics.browsers} labelKey="browser" />
                </section>
                <section className="border border-black/10 p-6 lg:col-span-2">
                  <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#C8102E] mb-6">— Countries</p>
                  <RankRows rows={analytics.countries} labelKey="country" max={15} />
                </section>
                <section className="border border-black/10 p-6 lg:col-span-2">
                  <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#C8102E] mb-6">— Daily Pageviews</p>
                  <div className="flex items-end gap-1 h-32">
                    {(() => {
                      const top = Math.max(1, ...analytics.daily.map((d) => d.count));
                      return analytics.daily.map((d) => (
                        <div key={d.day} className="flex-1 flex flex-col items-center gap-2">
                          <div className="w-full bg-[#C8102E]/80 hover:bg-[#C8102E] transition" style={{ height: `${(d.count / top) * 100}%` }} title={`${d.day}: ${d.count}`} />
                        </div>
                      ));
                    })()}
                  </div>
                  <p className="mt-4 font-mono-ui text-[9px] tracking-[0.25em] uppercase text-black/35">{analytics.daily.length} days in range</p>
                </section>
                {analytics.inquiry_breakdown.length > 0 && (
                  <section className="border border-black/10 p-6 lg:col-span-2">
                    <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#C8102E] mb-6">— Inquiry Breakdown</p>
                    <RankRows rows={analytics.inquiry_breakdown} labelKey="inquiry_type" />
                  </section>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "newsletter" && (
          <div data-testid="admin-newsletter-list">
            <TableHead cols={[
              { label: "Email", cls: "col-span-5" },
              { label: "Name", cls: "col-span-3" },
              { label: "Source", cls: "col-span-2" },
              { label: "Signed Up", cls: "col-span-2 text-right" },
            ]} />
            {newsletter.map((n) => (
              <div key={n.id} className="grid grid-cols-12 gap-4 py-4 border-b border-black/8 items-center">
                <div className="col-span-5 text-sm text-[#0a0a0a]/90">{n.email}</div>
                <div className="col-span-3 text-sm text-black/70">{n.name || "—"}</div>
                <div className="col-span-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-black/50">{n.source || "—"}</div>
                <div className="col-span-2 text-right font-mono-ui text-[10px] tracking-[0.2em] text-black/50">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
            {newsletter.length === 0 && <p className="mt-8 text-black/40 text-sm">No newsletter signups yet.</p>}
          </div>
        )}

        {tab === "inquiries" && (
          <div data-testid="admin-bookings-list" className="space-y-4">
            {bookings.map((b) => (
              <article key={b.id} className="border border-black/10 p-5 md:p-6">
                <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="font-mono-ui text-[10px] tracking-[0.25em] uppercase bg-[#C8102E] text-white px-2 py-1">{b.inquiry_type}</span>
                    <span className="font-display text-xl text-[#0a0a0a]">{b.name}</span>
                  </div>
                  <span className="font-mono-ui text-[10px] tracking-[0.2em] text-black/45">{new Date(b.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-black/65 mb-3">
                  <a href={`mailto:${b.email}`} className="hover:text-[#C8102E]">{b.email}</a>
                  {b.organization ? ` · ${b.organization}` : ""}
                </p>
                {(b.event_date || b.audience_size || b.budget) && (
                  <p className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-black/45 mb-3">
                    {b.event_date && `Date: ${b.event_date}`}
                    {b.audience_size && `  ·  Audience: ${b.audience_size}`}
                    {b.budget && `  ·  Budget: ${b.budget}`}
                  </p>
                )}
                <p className="text-[#0a0a0a]/80 leading-relaxed whitespace-pre-wrap">{b.message}</p>
              </article>
            ))}
            {bookings.length === 0 && <p className="mt-8 text-black/40 text-sm">No inquiries yet.</p>}
          </div>
        )}
      </main>
    </div>
  );
}
